import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchProjectFileScanningStatus as fetchProjectFileScanningStatusAPI } from '@/api/v4/project-files.api';
import { QueryKey } from '@/config/query-client';
import type { File } from '@/features/action-panel/files/file.interface';
import { notify } from '@/ui/snackbar/notify';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';

import { projectIdState } from '../../project.state';
import { useAtomValue } from 'jotai';

export const useProjectFileScanningStatus = (file: File) => {
  const { t } = useTranslation('default');
  const queryClient = useQueryClient();
  const projectId = useAtomValue(projectIdState);
  const [isError, setError] = useState(false);
  const { hasRequiredPermission } = useUserPermissions();

  const fetchFileScanningStatus = async () => {
    if (!projectId) return;
    return fetchProjectFileScanningStatusAPI({ projectId, fileId: file.id });
  };

  const query = useQuery({
    queryKey: [QueryKey.ProjectFileScanningStatus, projectId, file.id],
    queryFn: fetchFileScanningStatus,
    enabled:
      !!projectId &&
      hasRequiredPermission(PERMISSION.VIEW_FILES) &&
      file.scanningStatus === 'unscanned',
    staleTime: Infinity,
    refetchInterval: query => {
      const file = query.state.data;
      return !isError && file && file.scanningStatus === 'unscanned'
        ? 1000
        : false;
    },
  });

  const scanningStatus = query.data?.scanningStatus;

  useEffect(() => {
    if (!scanningStatus || scanningStatus === 'unscanned') return;

    void queryClient.invalidateQueries({
      queryKey: [QueryKey.ProjectFiles, projectId],
    });
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.ProjectActionsInfo, projectId],
    });

    if (scanningStatus === 'infected' || scanningStatus === 'error') {
      notify({
        message: t(
          scanningStatus === 'infected'
            ? 'actionPanel.files.infected'
            : 'actionPanel.files.scanningError',
          { name: file.name },
        ),
      });
    }
  }, [scanningStatus, queryClient, projectId, file.name, t]);

  useEffect(() => {
    if (query.isError) {
      setError(true);
      notify({ message: t`unknownError` });
    }
  }, [query.isError, t]);

  return query;
};
